import { getSessionToken } from '@shopify/app-bridge-utils';
import store from "@/V2/store/index";
import axios from 'axios';

declare global {
  interface Window {
    shopify: any;
  }
}

/**
 * 7 days in ms
 */
const ELIGIBILITY_TTL = 7 * 24 * 60 * 60 * 1000;

/**
 * Build a per-shop cache key
 * (Assumes you have a Shopify/shopDomain getter. If not, replace with whatever you store the shop domain in.)
 */
function getEligibilityCacheKey(): string {
  const shop = store.getters["Shopify/shop"] || "unknown";
  return `reviewEligibility:v1:${shop}`;
}

/**
 * Read from localStorage if still valid
 */
function getCachedEligibility(): { eligible: boolean; expiresAt: number; meta?: any } | null {
  const key = getEligibilityCacheKey();
  const raw = localStorage.getItem(key);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);

    if (!parsed.expiresAt || Date.now() > parsed.expiresAt) {
      localStorage.removeItem(key);
      return null;
    }

    return parsed;
  } catch {
    localStorage.removeItem(key);
    return null;
  }
}

/**
 * Save to localStorage (TTL = 7 days)
 */
function setCachedEligibility(eligible: boolean, meta: any = {}): void {
  const key = getEligibilityCacheKey();

  const payload = {
    eligible,
    cachedAt: Date.now(),
    expiresAt: Date.now() + ELIGIBILITY_TTL,
    meta,
  };

  localStorage.setItem(key, JSON.stringify(payload));
}

export default {

  /**
   * Attempts to request an App Store review.
   * After ANY attempt (regardless of Shopify response), we cache eligibility=false
   * so we don't try again from this browser for 7 days.
   */
  async maybeAskForReview(): Promise<void> {
    const eligible = await this.checkEligibilityCriteria();
    if (!eligible) return;

    const result = await window.shopify.reviews.request();

    // ✅ After any attempt, lock out further attempts for 7 days (regardless of success/decline)
    setCachedEligibility(false, {
      attempted: true,
      attemptedAt: Date.now(),
      lastCode: result?.code ?? null,
      success: result?.success ?? null,
    });

    await this.saveResult(result);
  },

  /**
   * Checks eligibility criteria, cached in localStorage for 7 days.
   * Returns boolean.
   */
  async checkEligibilityCriteria(): Promise<boolean> {

    // 1) Cache first
    const cached = getCachedEligibility();
    if (cached) {
      return !!cached.eligible;
    }

    // 2) Otherwise call API
    try {
      const app = store.getters["Shopify/app"];
      const session_token = await getSessionToken(app);

      const response = await axios.post(
        `${process.env.VUE_APP_API_SERVER}/api/v3/reviews/eligibility.json`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session_token}`,
            'App': process.env.VUE_APP_APP_NAME ?? '',
          }
        }
      );

      const minDays = 732; // adjust if needed (e.g. 730 for ~2 years)

      const eligible =
        Number(response.data?.daysInstalled || 0) >= minDays;

      // 3) Cache for 7 days
      setCachedEligibility(eligible, {
        daysInstalled: response.data?.daysInstalled ?? null,
      });

      return eligible;

    } catch (error) {
      console.log(error);

      // Cache negative result for 7 days to avoid hammering on repeated failures
      setCachedEligibility(false, { error: true });

      return false;
    }
  },

  /**
   * Records the attempt in your backend
   */
  async saveResult(result: any): Promise<void> {
    try {
      const app = store.getters["Shopify/app"];
      const session_token = await getSessionToken(app);

      await axios.post(
        `${process.env.VUE_APP_API_SERVER}/api/v3/reviews/attempt.json`,
        {
          code: result?.code ?? null,
          success: result?.success ?? null,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session_token}`,
            'App': process.env.VUE_APP_APP_NAME ?? '',
          }
        }
      );

    } catch (e) {
      console.log(e);
    }
  }
};
